import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';

type Img = { 
  id: string; 
  frameNo: number; 
  prompt: string; 
  status: 'queued' | 'generating' | 'uploading' | 'generated' | 'error'; 
  r2Key?: string | null;
  progress?: number;
  startedAt?: string | null;
  finishedAt?: string | null;
  lastError?: string | null;
  imageType?: string;
};

// Real-time updates hook
function useImageStream(taleId: string, onEvent: (ev: any) => void) {
  useEffect(() => {
    let closed = false;

    // Try SSE first
    const sse = new EventSource(`/api/images/stream?taleId=${taleId}`);
    sse.addEventListener('snapshot', (e: MessageEvent) => {
      onEvent({ type: 'snapshot', data: JSON.parse((e as any).data) });
    });
    sse.addEventListener('update', (e: MessageEvent) => {
      onEvent({ type: 'update', data: JSON.parse((e as any).data) });
    });
    sse.onerror = () => { sse.close(); fallbackPoll(); };

    // Fallback poll
    let since = Date.now();
    function fallbackPoll() {
      if (closed) return;
      const tick = async () => {
        try {
          const r = await fetch(`/api/images/${taleId}/updates?since=${since}`);
          const j = await r.json();
          (j.updates || []).forEach((u: any) => onEvent({ type: 'update', data: u }));
          since = j.now || since;
        } catch {}
        setTimeout(tick, 2000);
      };
      tick();
    }

    return () => { closed = true; sse.close(); };
  }, [taleId]);
}

export default function ImageGrid({ taleId }: { taleId: string }) {
  const [imgs, setImgs] = useState<Img[]>([]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef<number | null>(null);
  const index = useMemo(() => new Map(imgs.map(i => [i.id, i])), [imgs]);

  // Merge utility
  const upsert = useCallback((patch: Partial<Img> & { id: string }) => {
    setImgs(prev => {
      const m = new Map(prev.map(p => [p.id, p]));
      const cur = m.get(patch.id) || ({ id: patch.id } as Img);
      m.set(patch.id, { ...cur, ...patch });
      return Array.from(m.values()).sort((a, b) => (a.frameNo || 0) - (b.frameNo || 0));
    });
  }, []);

  // Live stream
  useImageStream(taleId, (ev) => {
    if (ev.type === 'snapshot') {
      // Filter out scene/background images - only show frame images
      const frameImages = (ev.data || []).filter((img: any) => 
        !img.imageType || img.imageType === 'frame'
      );
      setImgs(frameImages);
    } else if (ev.type === 'update') {
      const { imageId, status, progress, r2Key, lastError, startedAt, finishedAt, imageType } = ev.data;
      // Only update if it's a frame image (not a background/scene)
      if (!imageType || imageType === 'frame') {
        upsert({ id: imageId, status, progress, r2Key, lastError, startedAt, finishedAt, imageType });
      }
    }
  });

  const needsPolling = useMemo(
    () => imgs.some(i => i.status === 'queued' || i.status === 'generating' || i.status === 'uploading'),
    [imgs]
  );

  async function refresh() {
    const r = await fetch(`/api/images/${taleId}`);
    const j = await r.json();
    // Filter out scene/background images - only show frame images
    const frameImages = (j.items || []).filter((img: any) => 
      !img.imageType || img.imageType === 'frame'
    );
    setImgs(frameImages);
  }

  async function queue(variant: 'missing' | 'all' = 'missing') {
    setLoading(true);
    try {
      const r = await fetch('/api/images/prompts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taleId, variant })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'queue failed');
      // toast success would be nice here
      await refresh();
    } catch (e) {
      // toast error
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [taleId]);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (needsPolling) {
      pollRef.current = window.setInterval(refresh, 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [needsPolling]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button 
          className="px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-sm" 
          onClick={() => queue('missing')} 
          disabled={loading}
        >
          {loading ? 'Queueing…' : 'Requeue missing'}
        </button>
        <button 
          className="px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-sm" 
          onClick={() => queue('all')} 
          disabled={loading}
        >
          Queue all
        </button>
      </div>

      {imgs.length === 0 ? (
        <div className="text-sm text-muted-foreground">No images yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {imgs.map(img => (
            <ImageCard
              key={img.id}
              active={img.status === 'generating'}
              status={img.status}
              progress={img.progress ?? (img.status === 'generated' ? 100 : img.status === 'uploading' ? 90 : img.status === 'generating' ? 10 : 0)}
              prompt={img.prompt}
              src={img.r2Key ? `/api/images/file/${img.id}` : undefined}
              frameNo={img.frameNo}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Enhanced ImageCard component with tooltips and progress
function ImageCard({ src, status, progress, active, prompt, frameNo }: any) {
  return (
    <Tooltip.Provider delayDuration={200}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <div className={`relative border border-border rounded-xl p-3 ${active ? 'ring-2 ring-blue-500/35 shadow-lg' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
                status === 'generated' ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' :
                status === 'uploading' ? 'bg-blue-500/10 text-blue-300 border-blue-500/30' :
                status === 'generating' ? 'bg-amber-500/10 text-amber-300 border-amber-500/30' :
                status === 'error' ? 'bg-rose-500/10 text-rose-300 border-rose-500/30' :
                'bg-slate-500/10 text-slate-300 border-slate-500/30'
              }`}>
                {status}
              </span>
              {['queued', 'generating', 'uploading'].includes(status) && (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
            </div>

            <div className="text-xs mb-2 text-muted-foreground">Frame {frameNo + 1}</div>
            
            <div className="h-32 rounded overflow-hidden bg-neutral-900 cursor-help">
              {src ? (
                <img src={src} className="w-full h-full object-cover" alt={`Frame ${frameNo + 1}`} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-neutral-400">
                  {status === 'generating' ? 'Generating...' : 
                   status === 'uploading' ? 'Uploading...' : 
                   status === 'queued' ? 'Queued' : 
                   status === 'error' ? 'Error' : 'Ready'}
                </div>
              )}
            </div>

            {progress > 0 && progress < 100 && (
              <div className="absolute left-0 bottom-0 h-1 bg-current opacity-30" style={{ width: `${progress}%` }} />
            )}

            <div className="mt-2 text-[11px] line-clamp-2 opacity-70">{prompt}</div>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content className="max-w-[28rem] p-3 rounded-md bg-neutral-800 text-neutral-100 shadow-lg border border-neutral-700 text-sm leading-relaxed">
            <div className="whitespace-pre-line">{prompt}</div>
            <Tooltip.Arrow className="fill-neutral-800" />
          </Tooltip.Content>
        </Tooltip.Portal>
          </Tooltip.Root>
  </Tooltip.Provider>
);
}
